import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "sonner";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { useCartSync } from "../hooks/useCartSync";
import faviconAsset from "../assets/double-lion-favicon-full.png.asset.json";
import { SiteFooter } from "@/components/SiteFooter";
import { useRouterState } from "@tanstack/react-router";
import { trackMeta } from "@/lib/metaPixel";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Double Lion — Limited Edition" },
      { name: "description", content: "Double Lion — a limited edition release. Built for performance, made to last." },
      { property: "og:title", content: "Double Lion — Limited Edition" },
      { property: "og:description", content: "A limited edition release from Double Lion." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "google-site-verification", content: "F2DoKt5wS1DsbUYxKVeXHgKKDKmZ_ORP39dDUC-_UcI" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: faviconAsset.url },
      { rel: "apple-touch-icon", href: faviconAsset.url },
      // Preload primary web fonts (self-hosted, latin subset) to eliminate FOUT on the LCP heading.
      { rel: "preload", as: "font", type: "font/woff2", href: "/__l5e/assets-v1/e6373f0d-bc4d-4c44-9377-bc1f080ba251/InstrumentSerif-400.woff2", crossOrigin: "anonymous", fetchPriority: "high" as const },
      { rel: "preload", as: "font", type: "font/woff2", href: "/__l5e/assets-v1/652a05fc-630d-41c3-905e-3480f1f78ad1/IBMPlexMono-400.woff2", crossOrigin: "anonymous", fetchPriority: "high" as const },
      { rel: "preload", as: "font", type: "font/woff2", href: "/__l5e/assets-v1/04f72137-a7cd-4bc4-9d72-3879792915cd/Inter-400.woff2", crossOrigin: "anonymous" },
    ],
    scripts: [
      {
        children:
          "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-KFFBBJ3H');",
      },
      {
        type: "application/ld+json",

        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "Double Lion",
              url: "https://doublelion.com",
              logo: faviconAsset.url,
            },
            {
              "@type": "WebSite",
              name: "Double Lion",
              url: "https://doublelion.com",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-KFFBBJ3H"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="Google Tag Manager"
          />
        </noscript>
        {children}

        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useCartSync();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    void trackMeta("PageView");
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-[100dvh]">
        <Outlet />
      </div>
      <section className="mx-auto flex max-w-3xl items-center justify-center px-6 pt-4 pb-24 text-center">
        <p className="font-serif text-2xl md:text-4xl leading-snug">
          For everyday use and expeditions
        </p>
      </section>

      <Toaster position="top-center" />
      <SiteFooter />
    </QueryClientProvider>
  );
}

